import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Grid,
  Box,
  TextField,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { AppContext } from '../../../context/Context';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';
import Calculator from '../../../components/Calculator/Calculator';
import { SocketContext } from '../../../context/SocketContext';
import toast, { Toaster } from 'react-hot-toast';

import {
  MultipleStop as MultipleStopIcon,
  Payment as PaymentIcon,
  AddCard as AddCardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  NextPlan as NextPlanIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  HelpCenter as HelpCenterIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

import { REFUND_PROVIDERS } from '../../../constants/transactionTypes';
import { vendorTone, vendorPressSx } from '../../../theme/vendorTone';
import ClubHouseCard from '../../../assets/img/ch-card.png';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import '../../../assets/css/buttons.css';

// The flat colours the provider chips already use in the transaction lists
const PROVIDER_TONES = {
  CH: '#0d3b7a',
  WG: '#4a1070',
  'OK D.': '#004d40',
};

function ModalRefund() {
  const { t } = useTranslation();

  const {
    updateShowSpillerkonto,
    updatePayCashTemp,
    userInfo,
    updateTransactions,
    hall,
    operator,
    session,
    parentInfo,
    updateRefreshUser,
    updateIsLoadingScreen,
    printer,
    updateMaxAllowed,
    updateShowIsLimitSurpassedTransaction,
    updateShowModalRefund,
    updateFocusOnMainScreen,
  } = useContext(AppContext);

  //* DISABLE FOCUS ON MAIN SCREEN AND PREVENT ESCAPE BUTTON
  useEffect(() => {
    updateFocusOnMainScreen(false);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      updateFocusOnMainScreen(true);
    };
  }, []);

  const { socket } = useContext(SocketContext);

  useEffect(() => {
    updatePayCashTemp(0);
    updateRefreshUser(true);
  }, []);

  const [isLimits, setIsLimits] = useState(true);

  const [reason, setReason] = useState('');
  const handleReasonChange = (event) => {
    setReason(event.target.value);
  };
  const reasonRef = useRef(reason);
  useEffect(() => {
    reasonRef.current = reason;
  }, [reason]);

  // No default: 'CH' preselected is how every refund would keep being booked to CH
  const [provider, setProvider] = useState('');
  const providerRef = useRef(provider);
  useEffect(() => {
    providerRef.current = provider;
  }, [provider]);

  const handleIsLimitsChange = (event) => {
    setIsLimits(event.target.checked);
  };
  const isLimitsRef = useRef(isLimits);
  useEffect(() => {
    isLimitsRef.current = isLimits;
  }, [isLimits]);

  const [amountDeposit, setAmountDeposit] = useState(0);

  const amountDepositRef = useRef(amountDeposit);
  useEffect(() => {
    amountDepositRef.current = amountDeposit;
  }, [amountDeposit]);

  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const parentInfoRef = useRef(parentInfo);
  useEffect(() => {
    parentInfoRef.current = parentInfo;
  }, [parentInfo]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  useEffect(() => {
    const handleRefund = (data) => {
      if (!data) {
        updateIsLoadingScreen(false);
        toast.error(t('error.error'));
        return;
      }

      // Server rejects a refund with no reason, and an object is truthy
      if (data.ok === false) {
        updateIsLoadingScreen(false);
        toast.error(
          data.reason === 'providerRequired'
            ? t('sales.refundProviderRequired')
            : t('sales.cashMovementReasonRequired')
        );
        return;
      }

      updateTransactions({
        name: 'Refusjon',
        amount: amountDepositRef.current * -1,
        type: 'transaction',
      });

      if (parentInfoRef.current) {
        socket.emit('checkLimitStatus', {
          userInfo: parentInfoRef.current,
          bingoName: hallRef.current,
        });
      } else {
        socket.emit('checkLimitStatus', {
          userInfo: userInfoRef.current,
          bingoName: hallRef.current,
        });
      }
      setTimeout(() => {
        updateIsLoadingScreen(false);
        updateRefreshUser(true);
        updateShowModalRefund(false);
      }, 500);
    };

    socket.on('refund', handleRefund);

    return () => {
      socket.off('refund', handleRefund);
    };
  }, [socket]);

  const handleConfirm = () => {
    // Mandatory: an unexplained refund is money leaving with no trace of why
    if (!reasonRef.current?.trim()) {
      toast.error(t('sales.cashMovementReasonRequired'));
      return;
    }

    // Which supplier the money goes back for; the reports segregate refunds by it
    if (!providerRef.current) {
      toast.error(t('sales.refundProviderRequired'));
      return;
    }

    updateIsLoadingScreen(true);

    socket.emit('refund', {
      operator: operatorRef.current,
      session: sessionRef.current,
      bingoName: hallRef.current,
      description: 'Refusjon',
      provider: providerRef.current,
      amount: parseInt(amountDepositRef.current),
      userInfo: parentInfoRef.current
        ? parentInfoRef.current
        : userInfoRef.current,
      isLimits: isLimitsRef.current,
      reason: reasonRef.current,
    });
  };

  const handleCancel = () => {
    updateShowModalRefund(false);
  };

  const updateAmountDeposit = (value, isTotal) => {
    if (
      (amountDeposit === 0 || amountDeposit === '0') &&
      (value === '0' || value === '00')
    ) {
      return;
    }

    if (
      (amountDeposit === '0' || amountDeposit === 0) &&
      value !== 'backspace'
    ) {
      setAmountDeposit(value);
      return;
    }
    if (
      (amountDeposit === '0' || amountDeposit === 0) &&
      value === 'backspace'
    ) {
      return;
    }
    if (
      value === 'backspace' &&
      amountDeposit.toString().length === 1 &&
      (amountDeposit !== '0' || amountDeposit !== 0)
    ) {
      setAmountDeposit('0');
      return;
    }
    if (
      value === 'backspace' &&
      amountDeposit.toString().length > 1 &&
      (amountDeposit !== '0' || amountDeposit !== 0)
    ) {
      setAmountDeposit(
        amountDeposit
          .toString()
          .substring(0, amountDeposit.toString().length - 1)
      );

      return;
    }

    if (isTotal) {
      setAmountDeposit(value.toString());
      return;
    }

    setAmountDeposit(amountDeposit + value);
  };

  return (
    <div className="modal-overlay">
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          style: {
            zIndex: '9999999 !important',
            fontSize: '24px',
          },
        }}
        containerStyle={{
          zIndex: 99999999, // Ensure this value is higher than the modal's z-index
        }}
      />
      <div>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
          }}
        >
          <div
            className="custom-modal-2"
            style={{
              width: '1165px',
              height: '939px',
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                padding: '10px',
              }}
            >
              {/*HEADER*/}
              <Grid
                container
                sx={{
                  height: '100%',
                  width: '100%',
                  marginTop: 0,
                  marginLeft: 0,
                  justifyContent: 'center',
                  alignContent: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                }}
              >
                <>
                  <Grid
                    item
                    xs={6}
                    sx={{
                      padding: '10px',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <Grid
                      container
                      flexDirection="row"
                      sx={{
                        height: '100%',
                        width: '100%',
                        marginTop: 0,
                        marginLeft: 0,
                        justifyContent: 'center',
                        alignContent: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                      }}
                    >
                      {/* TITLE */}
                      <Grid
                        item
                        xs={12}
                        sx={{
                          padding: '10px',
                          height: '75px',
                          width: '100%',
                          marginTop: 0,
                          marginLeft: 0,
                          justifyContent: 'flex-start',
                          alignContent: 'center',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <h2
                          style={{
                            color: 'white',
                          }}
                        >
                          {t('header.refund')}
                        </h2>
                      </Grid>

                      {/* CARD LOGO */}
                      {/* Smaller than it was: the provider row below needs the 100px,
                          and a CH card is no longer the whole story of a refund */}
                      <Grid
                        item
                        xs={12}
                        sx={{
                          padding: '10px',
                          height: '80px',
                          width: '100%',
                          marginTop: 0,
                          marginLeft: 0,
                          justifyContent: 'flex-start',
                          alignContent: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 0,
                        }}
                      >
                        <Box
                          component="img"
                          sx={{
                            height: '80px',
                            width: 'auto',
                            paddingTop: 0,
                          }}
                          alt=""
                          src={ClubHouseCard}
                        />
                      </Grid>

                      {/* INPUTS */}
                      <Grid
                        container
                        flexDirection="row"
                        sx={{
                          height: 'calc(100% - 505px)',
                          width: '100%',
                          alignContent: 'space-around',
                        }}
                      >
                        {/*Input*/}
                        <Grid
                          item
                          xs={12}
                          sx={{
                            padding: '10px',
                            height: '100px',
                            width: '100%',
                            marginTop: 0,
                            marginLeft: 0,
                            justifyContent: 'center',
                            alignContent: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: 1,
                          }}
                        >
                          <Grid
                            container
                            flexDirection="row"
                            sx={{
                              height: '100%',
                              width: '100%',
                              marginTop: 0,
                              marginLeft: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Grid
                              item
                              xs={5}
                              sx={{
                                padding: '10px',
                                display: 'flex', // Use flex display for alignment
                                alignItems: 'center', // Vertically center the contents
                                justifyContent: 'center', // Horizontally center the contents
                                height: '75px',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {' '}
                                {/* Container for icon and text */}
                                <CurrencyExchangeIcon
                                  sx={{
                                    fontSize: '45px',
                                    marginRight: '10px', // Adjust spacing between icon and text
                                    color: 'white',
                                  }}
                                />
                                <div>
                                  {' '}
                                  {/* Container for the text */}
                                  <Typography
                                    sx={{
                                      fontSize: '19px',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      color: 'white',
                                    }}
                                  >
                                    {t('sales.refund')}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '15px', // Smaller font size for the second line
                                      color: 'white',
                                    }}
                                  >
                                    {t('sales.refundCash')}
                                  </Typography>
                                </div>
                              </div>
                            </Grid>
                            <Grid
                              item
                              xs={7}
                              sx={{
                                marginTop: 0,
                                padding: '10px',
                                alignContent: 'center',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <TextField
                                autoComplete="off"
                                value={
                                  amountDeposit === 0 || amountDeposit === '0'
                                    ? ''
                                    : amountDeposit
                                }
                                sx={{
                                  '& label': {
                                    position: 'relative !important',
                                    fontSize: '22px !important',
                                    color: 'white !important',
                                  },
                                  '& label.Mui-focused': {
                                    position: 'relative !important',
                                    color: 'white !important',
                                  },
                                  '& input': {
                                    color: 'white !important',
                                    border: '2px solid #2a3a6e',
                                    borderRadius: '4px',
                                    height: '50px !important',
                                    width: '275px !important',
                                    fontSize: '40px !important',
                                    textAlign: 'center',
                                  },
                                  '& input.Mui-focused': {
                                    color: 'white !important',
                                    border: '2px solid #2a3a6e',
                                    borderRadius: '4px',
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'transparent', // Change the border color or set to 'transparent'
                                    },
                                  },
                                  '& .MuiInputBase-input': {
                                    caretColor: 'transparent', // Makes the cursor transparent
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        {/*Provider*/}
                        <Grid
                          item
                          xs={12}
                          sx={{
                            padding: '10px',
                            height: '100px',
                            width: '100%',
                            marginTop: 0,
                            marginLeft: 0,
                            justifyContent: 'center',
                            alignContent: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: 1,
                          }}
                        >
                          <Grid
                            container
                            flexDirection="row"
                            sx={{
                              height: '100%',
                              width: '100%',
                              marginTop: 0,
                              marginLeft: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Grid
                              item
                              xs={5}
                              sx={{
                                padding: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                height: '75px',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginLeft: '-20px',
                                }}
                              >
                                <StorefrontIcon
                                  sx={{
                                    fontSize: '45px',
                                    marginRight: '10px',
                                    color: 'white',
                                  }}
                                />
                                <div>
                                  <Typography
                                    sx={{
                                      fontSize: '19px',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      color: 'white',
                                    }}
                                  >
                                    {t('sales.refundProvider')}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '15px',
                                      color: 'white',
                                    }}
                                  >
                                    {t('sales.refundProviderHelp')}
                                  </Typography>
                                </div>
                              </div>
                            </Grid>
                            <Grid
                              item
                              xs={7}
                              sx={{
                                marginTop: 0,
                                padding: '10px',
                                display: 'flex',
                                gap: '10px',
                                alignContent: 'center',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {REFUND_PROVIDERS.map((code) => (
                                <Button
                                  key={code}
                                  variant="contained"
                                  onClick={() => setProvider(code)}
                                  sx={{
                                    height: '70px',
                                    width: '95px',
                                    minWidth: '95px',
                                    padding: 0,
                                    borderRadius: '12px',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '18px',
                                    textTransform: 'none',
                                    ...vendorTone(PROVIDER_TONES[code]),
                                    ...vendorPressSx,
                                    // The unpicked ones fade rather than hide: three
                                    // tiles with one lit is the whole answer at a glance
                                    opacity: provider === code ? 1 : 0.35,
                                    border:
                                      provider === code
                                        ? '3px solid #ffffff'
                                        : '3px solid transparent',
                                  }}
                                >
                                  {code}
                                </Button>
                              ))}
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sx={{
                            padding: '10px',
                            height: '100px',
                            width: '100%',
                            marginTop: 0,
                            marginLeft: 0,
                            justifyContent: 'center',
                            alignContent: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: 1,
                          }}
                        >
                          <Grid
                            container
                            flexDirection="row"
                            sx={{
                              height: '100%',
                              width: '100%',
                              marginTop: 0,
                              marginLeft: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Grid
                              item
                              xs={5}
                              sx={{
                                padding: '10px',
                                display: 'flex', // Use flex display for alignment
                                alignItems: 'flex-start', // Vertically center the contents
                                justifyContent: 'flex-start', // Horizontally center the contents
                                height: '75px',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginLeft: '-20px',
                                }}
                              >
                                {' '}
                                {/* Container for icon and text */}
                                <HelpCenterIcon
                                  sx={{
                                    fontSize: '45px',
                                    marginRight: '10px', // Adjust spacing between icon and text
                                    color: 'white',
                                  }}
                                />
                                <div>
                                  {' '}
                                  {/* Container for the text */}
                                  <Typography
                                    sx={{
                                      fontSize: '19px',
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                      color: 'white',
                                    }}
                                  >
                                    {t('sales.reason')}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '15px', // Smaller font size for the second line
                                      color: 'white',
                                    }}
                                  ></Typography>
                                </div>
                              </div>
                            </Grid>
                            <Grid
                              item
                              xs={7}
                              sx={{
                                marginTop: 0,
                                padding: '10px',
                                alignContent: 'center',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <TextField
                                autoComplete="off"
                                value={reason}
                                onChange={handleReasonChange}
                                sx={{
                                  '& label': {
                                    position: 'relative !important',
                                    fontSize: '22px !important',
                                    color: 'white !important',
                                  },
                                  '& label.Mui-focused': {
                                    position: 'relative !important',
                                    color: 'white !important',
                                  },
                                  '& input': {
                                    color: 'white !important',
                                    border: '2px solid #2a3a6e',
                                    borderRadius: '4px',
                                    height: '50px !important',
                                    width: '275px !important',
                                    fontSize: '40px !important',
                                    textAlign: 'center',
                                  },
                                  '& input.Mui-focused': {
                                    color: 'white !important',
                                    border: '2px solid #2a3a6e',
                                    borderRadius: '4px',
                                  },
                                  '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'transparent', // Change the border color or set to 'transparent'
                                    },
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sx={{
                            padding: '10px',
                            height: '100px',
                            width: '100%',
                            marginTop: 0,
                            marginLeft: 0,
                            justifyContent: 'center',
                            alignContent: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: 1,
                          }}
                        >
                          <Grid
                            container
                            flexDirection="row"
                            sx={{
                              height: '100%',
                              width: '100%',
                              marginTop: 0,
                              marginLeft: 0,
                              display: 'flex',
                              justifyContent: 'flex-start',
                              alignContent: 'flex-start',
                              alignItems: 'flex-start',
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isLimits}
                                  onChange={handleIsLimitsChange}
                                  name="checked"
                                  color="primary"
                                  sx={{
                                    color: 'white',
                                    '&.Mui-checked': {
                                      color: 'primary.main',
                                    },
                                    '&.Mui-disabled': {
                                      color: 'grey',
                                    },
                                    '&.MuiCheckbox-root.Mui-disabled': {
                                      color: 'grey',
                                    },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  variant="h6"
                                  style={{
                                    color: 'white',
                                    marginLeft: '10px',
                                    marginTop: '5px',
                                  }}
                                >
                                  {t('sales.refundLimits')}
                                </Typography>
                              }
                            />
                            <div style={{ height: '42px' }}></div>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/*SHORTCUTS*/}
                      <Grid
                        item
                        xs={12}
                        sx={{
                          padding: '10px',
                          height: '335px',
                          width: '100%',
                          marginLeft: 0,
                          justifyContent: 'center',
                          alignContent: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          flexDirection: 'column',
                          marginTop: '10px',
                        }}
                      >
                        <h2
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            color: 'white',
                          }}
                        >
                          {t('sales.shortcuts')}
                        </h2>
                        <Grid
                          container
                          flexDirection="row"
                          sx={{
                            height: '100%',
                            width: '100%',
                            marginTop: 0,
                            marginLeft: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Grid
                            item
                            xs={12}
                            sx={{
                              border: '1px solid #2a3a6e',
                              borderRadius: '12px',
                              width: '100%',
                              maxWidth: '100% !important',
                              height: '100%',
                              backgroundColor: '#151d3b',
                            }}
                          >
                            <Grid
                              container
                              flexDirection="row"
                              sx={{
                                height: '100%',
                                width: '100%',
                                marginTop: 0,
                                marginLeft: 0,
                                display: 'flex',
                                alignContent: 'space-around',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Grid
                                item
                                xs={4}
                                sx={{
                                  display: 'flex',
                                  alignContent: 'center',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Grid
                                  item
                                  sx={{
                                    height: '100px',
                                    width: '100px',
                                    display: 'flex',
                                    alignContent: 'center',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      fontSize: '35px',
                                      fontWeight: 'bold',
                                      background:
                                        'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
                                      boxShadow:
                                        '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
                                      borderRadius: '12px',
                                      transition:
                                        'transform 80ms ease, box-shadow 80ms ease',
                                      '&:active': {
                                        transform: 'translateY(3px)',
                                        boxShadow:
                                          '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
                                      },
                                      color: 'white',
                                      marginTop: '0px',
                                    }}
                                    onClick={() => {
                                      setAmountDeposit(10);
                                    }}
                                  >
                                    10
                                  </Button>
                                </Grid>
                              </Grid>

                              <Grid
                                item
                                xs={4}
                                sx={{
                                  display: 'flex',
                                  alignContent: 'center',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Grid
                                  item
                                  sx={{
                                    height: '100px',
                                    width: '100px',
                                    display: 'flex',
                                    alignContent: 'center',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      fontSize: '35px',
                                      fontWeight: 'bold',
                                      background:
                                        'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
                                      boxShadow:
                                        '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
                                      borderRadius: '12px',
                                      transition:
                                        'transform 80ms ease, box-shadow 80ms ease',
                                      '&:active': {
                                        transform: 'translateY(3px)',
                                        boxShadow:
                                          '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
                                      },
                                      color: 'white',
                                      marginTop: '0px',
                                    }}
                                    onClick={() => {
                                      setAmountDeposit(20);
                                    }}
                                  >
                                    20
                                  </Button>
                                </Grid>
                              </Grid>

                              <Grid
                                item
                                xs={4}
                                sx={{
                                  display: 'flex',
                                  alignContent: 'center',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Grid
                                  item
                                  sx={{
                                    height: '100px',
                                    width: '100px',
                                    display: 'flex',
                                    alignContent: 'center',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      fontSize: '35px',
                                      fontWeight: 'bold',
                                      background:
                                        'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
                                      boxShadow:
                                        '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
                                      borderRadius: '12px',
                                      transition:
                                        'transform 80ms ease, box-shadow 80ms ease',
                                      '&:active': {
                                        transform: 'translateY(3px)',
                                        boxShadow:
                                          '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
                                      },
                                      color: 'white',
                                      marginTop: '0px',
                                    }}
                                    onClick={() => {
                                      setAmountDeposit(50);
                                    }}
                                  >
                                    50
                                  </Button>
                                </Grid>
                              </Grid>

                              <Grid
                                item
                                xs={4}
                                sx={{
                                  display: 'flex',
                                  alignContent: 'center',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Grid
                                  item
                                  sx={{
                                    height: '100px',
                                    width: '100px',
                                    display: 'flex',
                                    alignContent: 'center',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      fontSize: '35px',
                                      fontWeight: 'bold',
                                      background:
                                        'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
                                      boxShadow:
                                        '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
                                      borderRadius: '12px',
                                      transition:
                                        'transform 80ms ease, box-shadow 80ms ease',
                                      '&:active': {
                                        transform: 'translateY(3px)',
                                        boxShadow:
                                          '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
                                      },
                                      color: 'white',
                                      marginTop: '0px',
                                    }}
                                    onClick={() => {
                                      setAmountDeposit(100);
                                    }}
                                  >
                                    100
                                  </Button>
                                </Grid>
                              </Grid>

                              <Grid
                                item
                                xs={4}
                                sx={{
                                  display: 'flex',
                                  alignContent: 'center',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Grid
                                  item
                                  sx={{
                                    height: '100px',
                                    width: '100px',
                                    display: 'flex',
                                    alignContent: 'center',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      fontSize: '35px',
                                      fontWeight: 'bold',
                                      background:
                                        'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
                                      boxShadow:
                                        '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
                                      borderRadius: '12px',
                                      transition:
                                        'transform 80ms ease, box-shadow 80ms ease',
                                      '&:active': {
                                        transform: 'translateY(3px)',
                                        boxShadow:
                                          '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
                                      },
                                      color: 'white',
                                      marginTop: '0px',
                                    }}
                                    onClick={() => {
                                      setAmountDeposit(200);
                                    }}
                                  >
                                    200
                                  </Button>
                                </Grid>
                              </Grid>

                              <Grid
                                item
                                xs={4}
                                sx={{
                                  display: 'flex',
                                  alignContent: 'center',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Grid
                                  item
                                  sx={{
                                    height: '100px',
                                    width: '100px',
                                    display: 'flex',
                                    alignContent: 'center',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      fontSize: '35px',
                                      fontWeight: 'bold',
                                      background:
                                        'linear-gradient(160deg, #9dafbd 0%, #6d8190 100%)',
                                      boxShadow:
                                        '0 4px 12px rgba(136,156,171,0.2), inset 0 1px 0 rgba(255,255,255,0.14)',
                                      borderRadius: '12px',
                                      transition:
                                        'transform 80ms ease, box-shadow 80ms ease',
                                      '&:active': {
                                        transform: 'translateY(3px)',
                                        boxShadow:
                                          '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.1)',
                                      },
                                      color: 'white',
                                      marginTop: '0px',
                                    }}
                                    onClick={() => {
                                      setAmountDeposit(500);
                                    }}
                                  >
                                    500
                                  </Button>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    sx={{
                      padding: '10px',
                    }}
                  >
                    <Calculator updateAmount={updateAmountDeposit} />
                    <Grid
                      item
                      xs={12}
                      sx={{
                        width: '100%',
                        maxWidth: '100% !important',
                        height: '100%',
                      }}
                    >
                      <Grid
                        container
                        flexDirection="row"
                        sx={{
                          height: '125px',
                          width: '100%',
                          marginTop: 0,
                          marginLeft: 0,
                          padding: '10px',
                        }}
                      >
                        {/*Input*/}

                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <button
                            className="btn-custom-yes"
                            disabled={parseInt(amountDeposit) === 0}
                            onClick={handleConfirm}
                          >
                            <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                            {t('sales.confirm')}
                          </button>
                        </Grid>

                        <Grid
                          item
                          xs={6}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <button
                            className="btn-custom-no"
                            onClick={handleCancel}
                          >
                            <CancelIcon style={{ fontSize: '1.2em' }} />
                            {t('sales.cancel')}
                          </button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalRefund;
